import {
	AnimationCallbackEvent,
	Directive,
	ElementRef,
	InjectionToken,
	OnDestroy,
	OnInit,
	inject,
	input,
} from '@angular/core'
import { LeaveSyncCoordinator } from './leave-sync-coordinator.service'

const DEFAULT_CLASS = 'leave-sync-leaving'

/**
 * The class name(s) `LeaveSyncDirective` applies when no `[mosLeaveSync]` binding is present. A
 * component composing the directive via `hostDirectives` has no way to bind a template attribute to
 * itself, so it provides its own class(es) here instead - `hostDirectives`' `inputs: [...]` forwarding
 * would make the value externally bindable by *its* consumers, which isn't what a component-internal
 * default calls for.
 *
 * ```ts
 * @Component({
 *   hostDirectives: [LeaveSyncDirective],
 *   providers: [{ provide: LEAVE_SYNC_CLASS, useValue: 'progress-out' }],
 * })
 * ```
 */
export const LEAVE_SYNC_CLASS = new InjectionToken<string | string[]>('LeaveSyncClass', {
	factory: () => DEFAULT_CLASS,
})

/**
 * Syncs a component's `animate.leave` with any nested component's own leave animation, working
 * around native `animate.leave` not propagating across component boundaries (see
 * `LeaveSyncCoordinator`'s doc comment). Apply via `hostDirectives: [LeaveSyncDirective]` on any
 * component that either needs its own leave synced with an ancestor's, or needs to make sure a
 * nested child's leave is synced with its own - the same directive covers both roles, since it
 * doesn't need to know in advance which one it'll play for a given removal (see below).
 *
 * ## Usage
 *
 * ```ts
 * @Component({
 *   hostDirectives: [LeaveSyncDirective],
 *   providers: [{ provide: LEAVE_SYNC_CLASS, useValue: 'my-leave-class' }],
 *   host: { 'animate.enter': 'my-enter-class' }, // enter is unaffected, still plain string form
 *   styles: `:host(.my-leave-class) { animation: my-leave-keyframes 400ms both; }`,
 * })
 * export class MyComponent {}
 * ```
 *
 * No template wiring is needed at the call site in either direction - a component that self-declares
 * this directive automatically participates whether it ends up being removed on its own (it becomes
 * the trigger) or as part of an ancestor's removal (it gets synced as a descendant instead). Provide
 * `LEAVE_SYNC_CLASS` to name its own class(es) instead of the generic `leave-sync-leaving` default -
 * applied when the directive is used directly as a template attribute instead (e.g. a plain element
 * with no component of its own to provide anything), bind `[mosLeaveSync]` instead.
 *
 * ## How it works
 *
 * Every instance binds `(animate.leave)` identically. Only the outermost instance being removed in a
 * given single-shot removal ever actually has Angular invoke its binding - every other instance's own
 * binding silently never fires. That's fine: whichever one DOES fire becomes the trigger for that
 * removal and calls into the coordinator, which syncs everyone else it contains (and that shares its
 * scope) via their own registered `leave()` callback instead. `leave()` is reused unchanged for both
 * the trigger role and the synced-descendant role.
 *
 * Completion is derived from the real CSS Animation API instead of a hand-declared duration - right
 * after adding the class(es), `getAnimations()` reflects whatever just started (CSS keyframe
 * `animation` starts synchronously on a class add, unlike `transition`, which needs a style flush
 * first - so every leave class used with this directive must use `animation`, not `transition`, to be
 * picked up reliably in the same tick). `Animation.finished` already accounts for that animation's own
 * `animation-delay` plus its duration, so staggering multiple classes on one element via
 * `[mosLeaveSync]="['classA', 'classB']"` is just a matter of giving one of them a CSS
 * `animation-delay` - no JS-side delay bookkeeping needed.
 *
 * Already-`'finished'` animations are excluded from what `leave()` waits on (playState-based, not an
 * identity snapshot - an already-finished animation kept associated by a `both`/`forwards` fill mode
 * can end up reusing the same underlying `Animation` object once the class change swaps which keyframe
 * applies, so identity can't reliably tell old from new; playState can). No local anti-hang fallback
 * timeout is needed either - Angular's own `(animate.leave)` machinery already wraps this directive's
 * `onLeave()` in a `setTimeout` keyed off the public `MAX_ANIMATION_TIMEOUT` injection token
 * (`@angular/core`, defaults to 4000ms) that force-completes the leave regardless of whether this
 * promise chain (or any synced descendant's) ever settles.
 *
 * `[mosLeaveSyncScope]` opts a subset of a subtree into an exclusive sync group: a participant only
 * gets synced by a trigger whose own scope strictly equals its own (both default to `undefined`, so
 * plain usage is unaffected). Tag a trigger and specific descendants with the same custom scope string
 * to sync only them, leaving everything else - even if DOM-contained - alone.
 */
@Directive({
	selector: '[mosLeaveSync]',
	host: {
		'(animate.leave)': 'onLeave($event)',
	},
})
export class LeaveSyncDirective implements OnInit, OnDestroy {
	private readonly elementRef = inject(ElementRef<HTMLElement>)
	private readonly coordinator = inject(LeaveSyncCoordinator)
	private readonly injectedClass = inject(LEAVE_SYNC_CLASS)
	private unregister?: () => void
	private leaving = false

	readonly config = input<string | string[] | undefined>(undefined, { alias: 'mosLeaveSync' })
	readonly scope = input<string | undefined>(undefined, { alias: 'mosLeaveSyncScope' })

	ngOnInit(): void {
		this.unregister = this.coordinator.register({
			element: this.elementRef.nativeElement,
			scope: this.effectiveScope(),
			leave: () => this.leave(),
		})
	}

	ngOnDestroy(): void {
		// ngOnDestroy fires around the same moment as the leave animation *starting*, not finishing
		// (see angular/angular#19826) - unregistering synchronously here can race whichever instance's
		// own onLeave()/trigger() call is handling this same removal, pulling this participant out of
		// the registry before it's ever synced. The element is still physically in the DOM at this
		// point (animate.leave defers actual removal until animationComplete()), so deferring by a
		// tick is safe and lets anything already queued for this removal run against the full registry
		// first.
		setTimeout(() => {
			this.unregister?.()
		}, 0)
	}

	protected onLeave(event: AnimationCallbackEvent): void {
		if (this.leaving) return
		this.leaving = true

		const { animationComplete } = event
		const ownDone = this.leave()
		const descendantPromises = this.coordinator.trigger(this.elementRef.nativeElement, this.effectiveScope())

		Promise.all([ownDone, ...descendantPromises]).then(() => {
			animationComplete()
		})
	}

	private leave(): Promise<void> {
		const element = this.elementRef.nativeElement

		for (const className of this.classNames()) {
			element.classList.add(className)
		}

		// { subtree: true } - a component's own leave visual is often composed of plain nested elements
		// with their own staggered animations (e.g. ChapterTitleComponent's `.chapter-number`/
		// `.chapter-label`/`.chapter-title`), not necessarily an animation on the bound element itself.
		// Without subtree, getAnimations() would only see an animation directly on `element`, missing
		// those - and potentially resolving immediately if the bound element has no animation of its own
		// (e.g. a bare, unnamed `animation: 400ms;` duration marker, which creates no real Animation
		// since animation-name stays `none` - that marker only ever mattered for the old string-form
		// animate.leave's native duration-sniffing, not this directive's function-form).
		const active = element
			.getAnimations({ subtree: true })
			.filter((animation: Animation) => animation.playState !== 'finished')
		if (active.length === 0) return Promise.resolve()

		return Promise.all(active.map((animation: Animation) => animation.finished.catch(() => undefined))).then(
			() => undefined,
		)
	}

	private classNames(): string[] {
		const raw = this.config()
		// No [mosLeaveSync] binding, or a bare `mosLeaveSync` attribute (no `[mosLeaveSync]="..."`,
		// which binds this aliased input to the empty string rather than leaving it unset) - fall back
		// to whatever the composing component provided via LEAVE_SYNC_CLASS (or its own default).
		const effective = raw === '' || raw === undefined ? this.injectedClass : raw
		return Array.isArray(effective) ? effective : [effective]
	}

	private effectiveScope(): string | undefined {
		const raw = this.scope()
		// Same bare-attribute gotcha as mosLeaveSync - a bare `mosLeaveSyncScope` attribute binds to
		// the empty string, not undefined.
		return raw === '' ? undefined : raw
	}
}
